import React, { useState, useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { EventBus } from 'src/helpers/new';

interface UsePromptProps {
    when?: boolean;
    inExam: boolean;
    inQuiz: boolean;
    unloadCallback?: () => void;
}

const Prompt = ({ when = true, inExam = false, inQuiz = false, unloadCallback }: UsePromptProps) => {
    const [isPromptVisible, setIsPromptVisible] = useState(false);

    const blocker: any = useBlocker(({ currentLocation, nextLocation }) => {
        if (when && nextLocation.state?.avoidRouteLeavingGuard !== true) {
            setIsPromptVisible(true);
            return true;
        }
        return false;
    });

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (when) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('unload', () => {
            if (unloadCallback) unloadCallback();
        });
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('unload', () => {
                if (unloadCallback) unloadCallback();
            });
        };
    }, [when]);

    const handleConfirmNavigationClick = () => {
        setIsPromptVisible(false);
        if (inExam) {
            EventBus.dispatch('end-exam-proctoring');
        } else if (inQuiz) {
            EventBus.dispatch('leave-quiz');
        }
        blocker.proceed();
    };

    const closePrompt = () => {
        setIsPromptVisible(false);
        blocker?.reset();
    };

    const getTitleText = (inExam: boolean, inQuiz: boolean) => {
        if (inExam) return 'Leave Exam';
        if (inQuiz) return 'Leave Quiz?';
        return 'Unsaved Changes';
    };

    const getBodyText = (inExam: boolean, inQuiz: boolean) => {
        if (inExam) {
            return [
                <span key='1'>
                    {` Are you sure you want to exit the exam? Please note, the exam timer will continue running in the
                    background!`}
                    <br />
                </span>,
                <span key='2'>{`Your answers will be auto submitted once timer run out !!!`} </span>,
            ];
        }

        if (inQuiz) {
            return 'Are you sure you wish to leave? you will be able to resume later if your time has not expired';
        }

        return `Are you sure that you want to leave the current page? The changes that you made won't be saved.`;
    };

    const getConfirmButtonText = (inExam: boolean, inQuiz: boolean) => {
        if (inExam || inQuiz) {
            return 'Leave';
        }
        return 'Discard changes and leave';
    };

    const getCancelButtonText = (inExam: boolean, inQuiz: boolean) => {
        if (inExam || inQuiz) {
            return 'Cancel';
        }
        return 'Stay on page';
    };

    return isPromptVisible ? (
        <ConfirmationModal
            show={isPromptVisible}
            hideModal={closePrompt}
            confirmAction={handleConfirmNavigationClick}
            titleText={getTitleText(inExam, inQuiz)}
            bodyText={getBodyText(inExam, inQuiz)}
            confirmButtonText={getConfirmButtonText(inExam, inQuiz)}
            cancelButtonText={getCancelButtonText(inExam, inQuiz)}
        />
    ) : null;
};

export default Prompt;
